import { Form, FormLabel } from '@/components/forms';
import { descriptionRegex } from '@/constants/regex';
import { Textarea } from '@/design-system/textarea';
import { useDynamicLocale } from '@/hoslog-intl';
import { cn } from '@/lib/utils';
import { updateCustomerPaymentLogsPayload } from '@/services/customerPaymentLogs/customerPaymentLogs.mutation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import useUpdateCustomerPaymentLogs from '../hooks/useUpdateCustomerPaymentLogs';

type ConfirmFormProps = { mode: 'reject'; withdrawalId: string } | { mode: 'approve'; withdrawalId: string };

const ConfirmFormSchema = (isRequired: boolean) =>
  z.object({
    remarks: isRequired
      ? z
          .string()
          .min(1, { message: 'common.form.validation.required' })
          .trim()
          .max(200, { message: 'common.form.validation.maxLength' })
          .refine((val) => val == null || val === '' || descriptionRegex.test(val), {
            message: 'common.form.validation.nameOnly',
          })
      : z
          .string()
          .trim()
          .max(200, 'common.form.validation.maxLength')
          .refine((val) => val == null || val === '' || descriptionRegex.test(val), {
            message: 'common.form.validation.nameOnly',
          }),
  });

export const ConfirmForm = (props: ConfirmFormProps) => {
  const isRequired = props.mode === 'reject';
  const form = useForm<updateCustomerPaymentLogsPayload>({
    resolver: zodResolver(ConfirmFormSchema(isRequired)),
    mode: 'onBlur',
  });
  const {
    formState: { errors },
  } = form;
  const { t } = useDynamicLocale();
  const { mutateAsyncCustomerPaymentLogs } = useUpdateCustomerPaymentLogs();
  const handleSubmit = async (data: updateCustomerPaymentLogsPayload) => {
    await mutateAsyncCustomerPaymentLogs({
      withdrawalId: props.withdrawalId,
      action: props.mode,
      remarks: data.remarks.trim(),
    });
  };
  return (
    <Form form={form} onSubmit={handleSubmit}>
      <div className="flex flex-col gap-2 w-full pt-0.5 max-h-[500px] overflow-y-auto px-0.5 pb-1">
        {isRequired ? (
          <FormLabel>
            {t('common.form.label.remarks')} <span className="text-destructive text-[13px] font-bold">*</span>
          </FormLabel>
        ) : (
          <FormLabel>{t('common.form.label.remarks')}</FormLabel>
        )}
        <Textarea
          {...form.register('remarks')}
          name="remarks"
          placeholder={t('common.form.placeholder.remarks')}
          maxLength={200}
          className={cn(
            'h-[64px] disabled:opacity-50 disabled:bg-card placeholder:text-muted-foreground placeholder:font-medium placeholder:text-control leading-[24px] overflow-y-auto px-[13px] py-2',
            errors.remarks && 'border-destructive focus-visible:ring-destructive/20',
          )}
        />
        {errors.remarks && (
          <p className="text-sm text-destructive-text font-medium">{t(errors.remarks.message as string)}</p>
        )}
      </div>
    </Form>
  );
};
